/**
 * VRM Integration Service
 * Provides AI agents with direct control over VRM models, animations, and expressions
 */

import { VRMControlAction, CameraControlAction } from '../core/agent-controller';

export interface VRMAnimation {
  id: string;
  name: string;
  file: string;
  duration: number;
  category: 'idle' | 'gesture' | 'dance' | 'expression' | 'special';
  emotionalTone: 'neutral' | 'happy' | 'sad' | 'excited' | 'angry' | 'surprised';
  requiredTier: 'free' | 'premium' | 'vip';
}

export interface VRMExpression {
  id: string;
  name: string;
  morphTargets: Record<string, number>;
  duration: number;
  blendable: boolean;
}

export interface CameraPreset {
  id: string;
  name: string;
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  fov: number;
  description: string;
}

export class VRMIntegrationService {
  private availableAnimations: VRMAnimation[] = [
    {
      id: 'idle-basic',
      name: 'Basic Idle',
      file: 'idle-000.fbx',
      duration: 0,
      category: 'idle',
      emotionalTone: 'neutral',
      requiredTier: 'free',
    },
    {
      id: 'wave-hello',
      name: 'Wave Hello',
      file: 'wave-hello.fbx',
      duration: 2000,
      category: 'gesture',
      emotionalTone: 'happy',
      requiredTier: 'free',
    },
    {
      id: 'dance-basic',
      name: 'Basic Dance',
      file: 'dance.fbx',
      duration: 8000,
      category: 'dance',
      emotionalTone: 'excited',
      requiredTier: 'free',
    },
    {
      id: 'dance-hip-hop',
      name: 'Hip Hop Dance',
      file: 'dance-booty-hip-hop.fbx',
      duration: 10000,
      category: 'dance',
      emotionalTone: 'excited',
      requiredTier: 'premium',
    },
    {
      id: 'kiss',
      name: 'Blow Kiss',
      file: 'kiss.fbx',
      duration: 3000,
      category: 'gesture',
      emotionalTone: 'happy',
      requiredTier: 'vip',
    },
    {
      id: 'sleep',
      name: 'Sleep',
      file: 'sleep-000.fbx',
      duration: 0,
      category: 'special',
      emotionalTone: 'neutral',
      requiredTier: 'free',
    },
  ];

  private expressions: VRMExpression[] = [
    {
      id: 'smile',
      name: 'Smile',
      morphTargets: { mouth_smile: 1.0, eye_happy: 0.8 },
      duration: 3000,
      blendable: true,
    },
    {
      id: 'wink',
      name: 'Wink',
      morphTargets: { eye_wink_left: 1.0 },
      duration: 1000,
      blendable: true,
    },
    {
      id: 'surprise',
      name: 'Surprised',
      morphTargets: { mouth_open: 0.8, eye_wide: 1.0 },
      duration: 2000,
      blendable: true,
    },
    {
      id: 'blush',
      name: 'Blush',
      morphTargets: { blush: 1.0, eye_shy: 0.6 },
      duration: 4000,
      blendable: true,
    },
  ];

  private cameraPresets: CameraPreset[] = [
    {
      id: 'default',
      name: 'Default View',
      position: { x: 0, y: 0, z: 5 },
      rotation: { x: 0, y: 0, z: 0 },
      fov: 45,
      description: 'Standard full-body view',
    },
    {
      id: 'face-closeup',
      name: 'Face Closeup',
      position: { x: 0, y: 1.6, z: 2 },
      rotation: { x: 0, y: 0, z: 0 },
      fov: 35,
      description: 'Close view of face for expressions',
    },
    {
      id: 'upper-body',
      name: 'Upper Body',
      position: { x: 0, y: 0.8, z: 3 },
      rotation: { x: -5, y: 0, z: 0 },
      fov: 40,
      description: 'Focus on upper body and gestures',
    },
    {
      id: 'side-profile',
      name: 'Side Profile',
      position: { x: 3, y: 1, z: 2 },
      rotation: { x: 0, y: 45, z: 0 },
      fov: 40,
      description: 'Side view profile shot',
    },
    {
      id: 'dynamic-angle',
      name: 'Dynamic Angle',
      position: { x: 2, y: 2, z: 4 },
      rotation: { x: -15, y: 20, z: 0 },
      fov: 50,
      description: 'Cinematic angled view',
    },
  ];

  getAvailableAnimations(tier: 'free' | 'premium' | 'vip' = 'free'): VRMAnimation[] {
    const tierHierarchy = { free: 0, premium: 1, vip: 2 };
    const userLevel = tierHierarchy[tier];

    return this.availableAnimations.filter((anim) => tierHierarchy[anim.requiredTier] <= userLevel);
  }

  getAnimationsByEmotion(
    emotion: string,
    tier: 'free' | 'premium' | 'vip' = 'free'
  ): VRMAnimation[] {
    return this.getAvailableAnimations(tier).filter((anim) => anim.emotionalTone === emotion);
  }

  getAnimationsByCategory(
    category: string,
    tier: 'free' | 'premium' | 'vip' = 'free'
  ): VRMAnimation[] {
    return this.getAvailableAnimations(tier).filter((anim) => anim.category === category);
  }

  getRandomAnimation(
    emotion?: string,
    category?: string,
    tier: 'free' | 'premium' | 'vip' = 'free'
  ): VRMAnimation | null {
    let animations = this.getAvailableAnimations(tier);

    if (emotion) {
      animations = animations.filter((anim) => anim.emotionalTone === emotion);
    }

    if (category) {
      animations = animations.filter((anim) => anim.category === category);
    }

    if (animations.length === 0) return null;

    return animations[Math.floor(Math.random() * animations.length)];
  }

  getExpression(id: string): VRMExpression | null {
    return this.expressions.find((expr) => expr.id === id) || null;
  }

  getExpressionByEmotion(emotion: string): VRMExpression | null {
    const emotionToExpression: Record<string, string> = {
      happy: 'smile',
      excited: 'smile',
      surprised: 'surprise',
      shy: 'blush',
      playful: 'wink',
    };

    const expressionId = emotionToExpression[emotion];
    return expressionId ? this.getExpression(expressionId) : null;
  }

  getCameraPreset(id: string): CameraPreset | null {
    return this.cameraPresets.find((preset) => preset.id === id) || null;
  }

  getAllCameraPresets(): CameraPreset[] {
    return [...this.cameraPresets];
  }

  /**
   * Process a VRM control action and return the necessary data for execution
   */
  processVRMAction(
    action: VRMControlAction,
    userTier: 'free' | 'premium' | 'vip' = 'free'
  ): {
    success: boolean;
    data?: any;
    error?: string;
  } {
    try {
      switch (action.action) {
        case 'animation': {
          const animation = this.availableAnimations.find((anim) => anim.id === action.value);
          if (!animation) {
            return { success: false, error: `Animation ${action.value} not found` };
          }

          const tierHierarchy = { free: 0, premium: 1, vip: 2 };
          if (tierHierarchy[animation.requiredTier] > tierHierarchy[userTier]) {
            return {
              success: false,
              error: `Animation requires ${animation.requiredTier} tier`,
            };
          }

          return {
            success: true,
            data: {
              animationFile: `/models/fbx/animations/${animation.file}`,
              duration: action.duration || animation.duration,
              blendMode: action.blendMode || 'replace',
            },
          };
        }

        case 'expression': {
          const expression = this.getExpression(action.value);
          if (!expression) {
            return { success: false, error: `Expression ${action.value} not found` };
          }

          return {
            success: true,
            data: {
              morphTargets: expression.morphTargets,
              duration: action.duration || expression.duration,
              intensity: action.intensity || 1.0,
              blendable: expression.blendable,
            },
          };
        }

        case 'pose': {
          // Handle pose actions (static positions)
          return {
            success: true,
            data: {
              poseFile: `/models/fbx/poses/${action.value}.fbx`,
              duration: action.duration || 0,
            },
          };
        }

        case 'gesture': {
          // Handle gesture animations
          const gestureAnimation = this.getAvailableAnimations(userTier).find(
            (anim) => anim.category === 'gesture' && anim.id === action.value
          );

          if (!gestureAnimation) {
            return { success: false, error: `Gesture ${action.value} not found` };
          }

          return {
            success: true,
            data: {
              animationFile: `/models/fbx/animations/${gestureAnimation.file}`,
              duration: action.duration || gestureAnimation.duration,
              blendMode: 'additive',
            },
          };
        }

        default:
          return { success: false, error: `Unknown VRM action: ${action.action}` };
      }
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Process a camera control action
   */
  processCameraAction(action: CameraControlAction): {
    success: boolean;
    data?: any;
    error?: string;
  } {
    try {
      switch (action.action) {
        case 'preset': {
          const preset = this.getCameraPreset(action.value as string);
          if (!preset) {
            return { success: false, error: `Camera preset ${action.value} not found` };
          }

          return {
            success: true,
            data: {
              position: preset.position,
              rotation: preset.rotation,
              fov: preset.fov,
              duration: action.duration || 2000,
              easing: action.easing || 'ease-in-out',
            },
          };
        }

        case 'position': {
          return {
            success: true,
            data: {
              position: action.value,
              duration: action.duration || 2000,
              easing: action.easing || 'ease-in-out',
            },
          };
        }

        case 'zoom': {
          return {
            success: true,
            data: {
              zoom: action.value,
              duration: action.duration || 1500,
              easing: action.easing || 'ease-in-out',
            },
          };
        }

        case 'focus': {
          return {
            success: true,
            data: {
              target: action.value,
              duration: action.duration || 1000,
            },
          };
        }

        default:
          return { success: false, error: `Unknown camera action: ${action.action}` };
      }
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get AI-suggested actions based on conversation context
   */
  suggestActionsForContext(
    message: string,
    emotion: string,
    userTier: 'free' | 'premium' | 'vip' = 'free'
  ): Array<VRMControlAction | CameraControlAction> {
    const actions: Array<VRMControlAction | CameraControlAction> = [];
    const lowerMessage = message.toLowerCase();

    // Suggest VRM animations based on keywords
    if (lowerMessage.includes('dance')) {
      const danceAnim = this.getRandomAnimation('excited', 'dance', userTier);
      if (danceAnim) {
        actions.push({
          type: 'vrm',
          action: 'animation',
          value: danceAnim.id,
          duration: danceAnim.duration,
        });
      }
    }

    if (lowerMessage.includes('hello') || lowerMessage.includes('hi')) {
      actions.push({
        type: 'vrm',
        action: 'gesture',
        value: 'wave-hello',
        duration: 2000,
      });
    }

    // Suggest expressions based on emotion
    const expression = this.getExpressionByEmotion(emotion);
    if (expression) {
      actions.push({
        type: 'vrm',
        action: 'expression',
        value: expression.id,
        duration: expression.duration,
      });
    }

    // Suggest camera movements based on context
    if (lowerMessage.includes('closer') || lowerMessage.includes('face')) {
      actions.push({
        type: 'camera',
        action: 'preset',
        value: 'face-closeup',
        duration: 2000,
      });
    }

    if (lowerMessage.includes('full body') || lowerMessage.includes('step back')) {
      actions.push({
        type: 'camera',
        action: 'preset',
        value: 'default',
        duration: 2000,
      });
    }

    return actions;
  }
}
